/* Parts Diagram interactive overlay — WS-PT-SCHEMATICS
 * Depends on: schematic-catalog.js (sets window.SCHEMATIC_CATALOG)
 */
(function () {
  "use strict";

  const BRAND_PURPLE = "#6B2D8B";
  const GREY         = "#999";
  const HOTSPOT_R    = 9;   // px in SVG viewport units
  const LABEL_SIZE   = 7;   // px

  // Crop boxes are per-machine because each catalog page scan has its own
  // schematic layout (width varies between ~0.32 and ~0.50 of the source).
  // Resolution order: entry.crop_box > CROP_BOX_BY_MACHINE > DEFAULT_CROP_BOX.
  // Source JPGs have title/footer/parts-table/mid-banner painted white per
  // scripts/migration/paint-schematic-regions.py. crop_w here matches each
  // page's paint_x (table-left edge), so the visible area is exactly:
  // schematic + small whitespace gap, with the painted regions fully hidden.
  // For dual-machine pages, crop_w is the wider of the two schematics' edges.
  const DEFAULT_CROP_BOX = { x: 0, y: 0.07, w: 0.50, h: 0.94 };
  const CROP_BOX_BY_MACHINE = {
    "GoFit 3, 6, 6 PLUS, 6 HEPA & 10":        { x: 0, y: 0.07, w: 0.45, h: 0.94 },
    "GoFit 3 & 6 Cordless":                    { x: 0, y: 0.07, w: 0.49, h: 0.94 },
    "GoFree Flex Pro II":                      { x: 0, y: 0.07, w: 0.52, h: 0.94 },
    "Super Coach Pro 6 & 10 / ProVac FS 6":    { x: 0, y: 0.07, w: 0.49, h: 0.94 },
    "Super CoachVac / Super QuarterVac":       { x: 0, y: 0.07, w: 0.50, h: 0.94 },
    "ProGen 12/15 (Page 1)":                   { x: 0, y: 0.07, w: 0.53, h: 0.94 },
    "ProGen 12/15 (Page 2)":                   { x: 0, y: 0.07, w: 0.49, h: 0.94 },
    "ProForce 1500XP/1200XP (Page 1)":         { x: 0, y: 0.07, w: 0.49, h: 0.94 },
    "ProForce 1500XP/1200XP (Page 2)":         { x: 0, y: 0.07, w: 0.50, h: 0.94 },
    "ProBlitz AirMover / ProGuard 4 Portable": { x: 0, y: 0.06, w: 0.51, h: 0.96 },
    "ProGuard 10 / ProGuard 16 MD":            { x: 0, y: 0.06, w: 0.49, h: 0.96 },
    "ProGuard 15/20":                          { x: 0, y: 0.07, w: 0.50, h: 0.94 }
  };

  function init() {
    const container = document.querySelector("[data-parts-diagram]");
    if (!container) return;

    const machineKey = container.dataset.partsDiagram;
    const catalog    = window.SCHEMATIC_CATALOG;
    if (!catalog || !catalog[machineKey]) {
      console.warn("Parts diagram: no catalog entry for", machineKey);
      return;
    }

    const entry  = catalog[machineKey];
    const isDual = entry.type === "dual";

    applyCropBox(container, entry, machineKey);
    renderPartsTable(container, entry, isDual);
    renderSvgOverlay(container, entry, isDual);
  }

  function applyCropBox(container, entry, machineKey) {
    const diagramEl = container.querySelector(".pt-pd-diagram");
    if (!diagramEl) return;
    const cb = entry.crop_box || CROP_BOX_BY_MACHINE[machineKey] || DEFAULT_CROP_BOX;
    diagramEl.style.setProperty("--crop-x", cb.x);
    diagramEl.style.setProperty("--crop-y", cb.y);
    diagramEl.style.setProperty("--crop-w", cb.w);
    diagramEl.style.setProperty("--crop-h", cb.h);
  }

  // ── Parts Table ──────────────────────────────────────────────────────────

  function renderPartsTable(container, entry, isDual) {
    const partsPanel = container.querySelector(".pt-pd-parts");
    if (!partsPanel) return;

    partsPanel.innerHTML = "";

    if (isDual) {
      // Group parts by machine_section
      const sectionNames = (entry.sections || []).map(s => s.name);
      const groups = { 0: [], 1: [] };
      entry.parts.forEach(p => {
        const sec = (p.machine_section !== undefined) ? p.machine_section : 0;
        if (!groups[sec]) groups[sec] = [];
        groups[sec].push(p);
      });

      [0, 1].forEach(sec => {
        const heading = document.createElement("h3");
        heading.className = "pt-pd-machine-heading";
        heading.textContent = sectionNames[sec] || `Machine ${sec + 1}`;
        partsPanel.appendChild(heading);

        const table = buildTable(groups[sec]);
        partsPanel.appendChild(table);
      });
    } else {
      const table = buildTable(entry.parts);
      partsPanel.appendChild(table);
    }
  }

  function buildTable(parts) {
    const table = document.createElement("table");
    table.className = "pt-pd-parts__table";

    const thead = table.createTHead();
    const hr    = thead.insertRow();
    ["#", "Part Number", "Description", ""].forEach(h => {
      const th = document.createElement("th");
      th.textContent = h;
      hr.appendChild(th);
    });

    const tbody = table.createTBody();
    // deduplicate: one row per (callout+part_number) pair
    const seen = new Set();
    parts.forEach(part => {
      const key = `${part.callout}|${part.part_number}`;
      if (seen.has(key)) return;
      seen.add(key);

      const tr = tbody.insertRow();
      if (part.callout) tr.dataset.callout = part.callout;
      if (part.machine_section !== undefined) tr.dataset.section = part.machine_section;

      // callout badge
      const tdBadge = tr.insertCell();
      if (part.callout) {
        const badge = document.createElement("span");
        badge.className = `pt-pd-callout-badge pt-pd-callout-badge--${part.in_netsuite ? "stocked" : "unstocked"}`;
        badge.textContent = part.callout;
        tdBadge.appendChild(badge);
      }

      // part number
      const tdPN = tr.insertCell();
      tdPN.className = "pt-pd-part-number";
      tdPN.textContent = part.part_number;

      // description
      const tdName = tr.insertCell();
      if (part.in_netsuite && part.handle) {
        const a = document.createElement("a");
        a.className = "pt-pd-part-link";
        a.href      = "/products/" + part.handle;
        a.textContent = part.name;
        tdName.appendChild(a);
      } else {
        const span = document.createElement("span");
        span.className = "pt-pd-part-name";
        span.textContent = part.name;
        tdName.appendChild(span);
      }

      // CTA
      const tdCta = tr.insertCell();
      if (part.in_netsuite && part.handle) {
        const a = document.createElement("a");
        a.className = "pt-pd-part-link";
        a.href      = "/products/" + part.handle;
        a.textContent = "View";
        tdCta.appendChild(a);
      } else if (!part.in_netsuite) {
        const a = document.createElement("a");
        a.className  = "pt-pd-contact-link";
        a.href       = "/pages/contact";
        a.textContent = "Contact";
        tdCta.appendChild(a);
      }
    });

    return table;
  }

  // ── SVG Overlay ──────────────────────────────────────────────────────────

  function renderSvgOverlay(container, entry, isDual) {
    const imgEl = container.querySelector(".pt-pd-diagram__img");
    const svgEl = container.querySelector(".pt-pd-diagram__svg");
    if (!imgEl || !svgEl) return;

    // Wait for image natural dimensions
    function buildSvg() {
      const iw = imgEl.naturalWidth  || imgEl.offsetWidth;
      const ih = imgEl.naturalHeight || imgEl.offsetHeight;
      svgEl.setAttribute("viewBox", `0 0 ${iw} ${ih}`);
      svgEl.innerHTML = "";

      const tooltip = document.getElementById("pt-pd-tooltip");

      // deduplicate by callout+section
      const rendered = new Set();

      entry.parts.forEach(part => {
        if (!part.callout || !part.coord) return;
        const sec  = part.machine_section !== undefined ? part.machine_section : 0;
        const key  = `${sec}|${part.callout}`;
        if (rendered.has(key)) return;
        rendered.add(key);

        const cx = part.coord.x * iw;
        const cy = part.coord.y * ih;
        if (cy > ih || cy < 0 || cx > iw || cx < 0) return;

        const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
        g.dataset.callout  = part.callout;
        if (isDual) g.dataset.section = sec;

        const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
        circle.setAttribute("cx", cx);
        circle.setAttribute("cy", cy);
        circle.setAttribute("r",  HOTSPOT_R);
        circle.className.baseVal = `pt-pd-hotspot pt-pd-hotspot--${part.in_netsuite ? "stocked" : "unstocked"}`;

        const label = document.createElementNS("http://www.w3.org/2000/svg", "text");
        label.setAttribute("x", cx);
        label.setAttribute("y", cy);
        label.setAttribute("font-size", LABEL_SIZE);
        label.className.baseVal = "pt-pd-hotspot__label";
        label.textContent = part.callout;

        g.appendChild(circle);
        g.appendChild(label);
        svgEl.appendChild(g);

        // tooltip + row highlight
        g.addEventListener("mouseenter", (e) => {
          if (tooltip) {
            tooltip.textContent = `${part.callout} — ${part.name} (${part.part_number})`;
            tooltip.classList.add("pt-pd-tooltip--visible");
          }
          highlightRows(container, part.callout, sec, isDual, true);
        });
        g.addEventListener("mousemove", (e) => {
          if (tooltip) {
            tooltip.style.left = (e.clientX + 14) + "px";
            tooltip.style.top  = (e.clientY - 10) + "px";
          }
        });
        g.addEventListener("mouseleave", () => {
          if (tooltip) tooltip.classList.remove("pt-pd-tooltip--visible");
          highlightRows(container, part.callout, sec, isDual, false);
        });

        // click navigates to product (stocked) or contact (unstocked)
        g.style.cursor = part.handle ? "pointer" : "default";
        if (part.handle) {
          g.addEventListener("click", () => {
            window.location.href = "/products/" + part.handle;
          });
        }
      });
    }

    if (imgEl.complete && imgEl.naturalWidth) {
      buildSvg();
    } else {
      imgEl.addEventListener("load", buildSvg);
    }
  }

  function highlightRows(container, callout, section, isDual, on) {
    const tbody = container.querySelectorAll(".pt-pd-parts__table tbody tr");
    tbody.forEach(tr => {
      if (tr.dataset.callout !== callout) return;
      if (isDual && tr.dataset.section !== String(section)) return;
      tr.classList.toggle("is-highlighted", on);
    });
  }

  // ── Bootstrap ────────────────────────────────────────────────────────────

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
